<template>
  <v-list-tile
    avatar
    class="item u-file-item"
    :class="{
      pending: item.state.equals(FILE_STATES.pending),
      uploading: item.state.equals(FILE_STATES.uploading),
      canceled: item.state.equals(FILE_STATES.canceled) || retries < 6,
      error: item.state.equals(FILE_STATES.error) && retries >= 6,
      completed: item.state.equals(FILE_STATES.completed)
    }" >
    <v-list-tile-avatar>
      <format-badge key="icon" v-if="!item.state.equals(FILE_STATES.uploading)" class="card-format" :extension="extension"/>
      <span v-else key="progress" class="icon icon-state progress">
        <span class="wrapper">
          <svg class="progress-circle" width="100%" height="100%" viewBox="0 0 120 120">
            <circle class="meter" cx="60" cy="60" r="54" stroke-width="12"></circle>
            <circle class="value" cx="60" cy="60" r="54" stroke-width="12"
              :style="{
                'stroke-dashoffset': item.progress? -((337 * item.progress) / 100) + 337 : 337
              }"></circle>
          </svg>
        </span>
        {{item.progress?parseInt(item.progress):0}}
      </span>
    </v-list-tile-avatar>
    <v-list-tile-content>
      <v-list-tile-title>
        <span class="file-title name" >{{item.file.name}}</span>
        <span class="file-title size" > | {{formatBytes(item.file.size)}}</span>
      </v-list-tile-title>
    </v-list-tile-content>
    <v-list-tile-action class="actions" >
      <v-btn
        :disabled="actionIcon === 'restore'"
        ripple
        @click.prevent.stop="action"
        class="action"
        :class="{
          'success': actionIcon === 'check',
          'info': actionIcon === 'refresh',
          'error': actionIcon === 'clear'
        }"
        icon>
        <v-icon>{{actionIcon}}</v-icon>
      </v-btn>
    </v-list-tile-action>
  </v-list-tile>
</template>

<script>
  import Color from '~/helpers/color.js'
  import $state from '../upload-state.js'
  import FILE_STATES from '../file-states.js'
  import FormatBadge from '~/components/common/files/FormatBadge.vue'

  export default {
    name: 'u-file-item',
    components: {FormatBadge},
    props: {
      item: {
        type: Object,
        required: true
      },
      uid: {
        type: String,
        required: true
      }
    },
    data () {
      return {
        'FILE_STATES': FILE_STATES
      }
    },
    watch: {
      status: {
        handler (val) {
          if (val === 'error' && this.retries < 6) {
            setTimeout(() => {
              this.retries++
              this.uploadFile()
            }, 3000 * (this.retries + 1))
          }
        },
        immediate: true
      }
    },
    computed: {

      status () {
        return this.item.state.id
      },
      retries: {
        get () {
          return this.item.retries
        },
        set (val) {
          $state.updateFileRetries(val, this.uid)
        }
      },
      extension () {
        let pieces = this.item.file.name.split('.')
        if (this.item.endpoint.endsWith('dicom')) {
          // we go this check because some dicoms do not have any extension
          return 'DCM'
        }

        return pieces.length > 1 && pieces[pieces.length - 1] ? pieces[pieces.length - 1].toUpperCase() : '?'
      },
      actionIcon () {
        let action

        switch (this.item.state.id) {
          case 'pending':
          case 'uploading':
            action = 'clear'
            break
          case 'canceled':
            action = 'refresh'
            break
          case 'error':
            if (this.retries < 6) {
              action = 'restore'
            } else {
              action = 'refresh'
            }
            break
          case 'completed':
            action = 'check'
            break
        }

        return action
      },
      action () {
        return event => {
          switch (this.item.state.id) {
            case 'pending':
            case 'uploading':
              $state.abortUpload(this.uid)
              break
            case 'canceled':
              this.uploadFile()
              break
            case 'error':
              if (this.retries >= 6) {
                $state.updateFileRetries(0, this.uid)
                this.uploadFile()
              }
              break
          }
        }
      }
    },
    methods: {
      colorFromExtension: Color.fromExtension,
      colorFromName: Color.fromName,
      formatBytes (bytes) {
        const thresh = 1024
        if (Math.abs(bytes) < thresh) {
          return bytes + ' B'
        }
        const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        let u = -1
        do {
          bytes /= thresh
          ++u
        } while (Math.abs(bytes) >= thresh && u < units.length - 1)
        return bytes.toFixed(1) + ' ' + units[u]
      },
      uploadFile () {
        $state.uploadFile(this.uid)
          .catch(e => {
            console.debug(e)
          }
        )
      },
      updateState (nextState) {
        if (nextState !== undefined) {
          $state.updateFileState(this.uid, nextState)
        }
      }
    }
  }
</script>
<style lang="scss">
  .u-file-item {
    &.item a {
      padding: 0 0 0 .5rem;
    }

    &.uploading {
      .avatar {
        padding-left: .5rem;
      }
    }

    .list__tile__content {
      padding-left: .25rem;
    }

    .card-format {
      max-width: 60px;
      border-radius: 2px;
      padding: 2px 6px;
      color: white;
      text-shadow: 0px 1px 1px #333;
      text-align: center;

      .format {
        width: 42px;
      }
    }

    .icon-state {
      color: white;
    }

    .actions {
      justify-content: center !important;

      .action {
        color: white;
        width: 26px;
        height: 26px;
      }
    }

    .file-title {
      display: inline-block;
      line-height: 2rem;
      vertical-align: middle;

      &.name {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        max-width: 250px;
      }

      &.size {
        display: inline;
      }
    }

    .extension {
      font-size: 1rem;
    }

    .progress {
      background-color: transparent;
      position: relative;
      color: rgba(0,0,0,0.54);

      .wrapper {
        position: absolute;
        left: -6px;
        top: -6px;
        right: -6px;
        bottom: -6px;

        .progress-circle {
          transform: rotate(-90deg);

          .meter {
            stroke: #e6e6e6;
            fill: none;
          }
          .value {
            stroke: #f77a52;
            fill: none;
            stroke-dasharray: 337;
            transition: .1s linear;
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/plugins/uploader/components/UFileItem.vue